import { useEffect, useRef } from 'react';

import { TABBABLE_ELEMENTS_SELECTOR } from 'constants/selectors';

const getFocusableElements = container => {
  if (container instanceof HTMLElement) {
    const focusableElements = container.querySelectorAll(TABBABLE_ELEMENTS_SELECTOR);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    return {
      focusableElements,
      firstFocusableElement,
      lastFocusableElement
    };
  } else {
    return {};
  }
};

const generateTarget = (current, elements, isShift) => {
  const currentIndex = elements.indexOf(current);
  let newTargetIndex;
  if (isShift) {
    newTargetIndex = currentIndex > 0 ? currentIndex - 1 : elements.length - 1;
  } else {
    newTargetIndex = (currentIndex + 1) % elements.length;
  }
  return elements[newTargetIndex];
};

const validateTrap = ({ target, focusableElements, originalElement, isShift }) => {
  if (!focusableElements.length) {
    return;
  }
  // we have to manually put this at the end of the call stack so new document.activeElement points to the newly focused item;
  setTimeout(() => {
    const currentElement = document.activeElement;
    const outOfTrap = !focusableElements.includes(currentElement);
    const isStuck = originalElement === currentElement;
    if (outOfTrap || isStuck) { // If the active element is out of the trap or is stuck, focus the intended target and revalidate
      target.focus();
      const newTarget = generateTarget(target, focusableElements, isShift);
      validateTrap({ target: newTarget, focusableElements, originalElement, isShift }); // we want to make sure future iterations of this point to a new target, since target.focus() has the potential to be have an unfocusable parent
    }
  }, 1); // This has to be set to something > 0 for browser compatibility
};

const trap = ({ ref, shouldFocusFirstElement }) => {
  const el = ref.current;

  if (!el) {
    return;
  }

  const returnFocusElement = document.activeElement;

  if (shouldFocusFirstElement) {
    const { firstFocusableElement } = getFocusableElements(el);
    firstFocusableElement && firstFocusableElement.focus();
  }

  const handleKeydown = e => {
    const isTabPressed = e.key === 'Tab';

    if (!isTabPressed) {
      return;
    }

    e.stopPropagation();
    const { firstFocusableElement, lastFocusableElement, focusableElements } = getFocusableElements(el);
    const elements = Array.from(focusableElements || []);
    const { activeElement } = document;
    const isShift = e.shiftKey;
    const target = generateTarget(activeElement, elements, isShift);

    if ((!isShift && activeElement === lastFocusableElement) || (isShift && activeElement === firstFocusableElement)) {
      e.preventDefault();
      target.focus();
    }

    validateTrap({ focusableElements: elements, target, originalElement: activeElement, isShift });
  };

  el.addEventListener('keydown', handleKeydown);

  return () => {
    returnFocusElement.focus();
    el.removeEventListener('keydown', handleKeydown);
  };
};

const useFocusTrap = ({ shouldFocusFirstElement = false, active = true }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!active) {
      return;
    }
    return trap({ ref, shouldFocusFirstElement });
  }, [active, shouldFocusFirstElement]
  );
  return ref;
};

export default useFocusTrap;
